// Settings.jsx
// App settings — Breeze API credentials, market timings, 
// download schedule, API limits

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

import API_BASE from '../config';
import { api } from '../api';

// ── Preflight — check symbols before starting a download job ──
const runPreflight = async (source, symbols, watchlistName) => {
  const params = { source };
  if (symbols && symbols.trim()) params.symbols = symbols.trim().toUpperCase();
  else if (watchlistName)        params.watchlist_name = watchlistName;
  const res = await api.get('/download/preflight', { params });
  return res.data;
};

function PreflightPanel({ data, onConfirm, onCancel, busy }) {
  if (!data) return null;

  const authFails = Object.entries(data.auth || {})
    .filter(([, a]) => !a.ok);

  return (
    <div style={{
      marginTop: '12px', padding: '12px 14px',
      border: `1px solid ${authFails.length ? '#e59a9a' : '#f0c36d'}`,
      borderRadius: '6px',
      background: authFails.length ? '#fff0f0' : '#fff8e6',
    }}>
      {authFails.length > 0 && (
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '13px', color: '#a33',
                        fontWeight: 'bold', marginBottom: '4px' }}>
            ✖ Provider credentials
          </div>
          <ul style={{ margin: '0 0 0 18px', padding: 0,
                       fontSize: '13px', color: '#444' }}>
            {authFails.map(([name, a]) => (
              <li key={name}><strong>{name}</strong> — {a.message}</li>
            ))}
          </ul>
        </div>
      )}

      {data.blocked.length > 0 && (
        <>
          <div style={{ fontSize: '13px', color: '#8a6d1a',
                        marginBottom: '8px' }}>
            ⚠ {data.blocked.length} of {data.total} symbol(s) cannot be
            downloaded with source "{data.source}" and will be skipped:
          </div>
          <ul style={{ margin: '0 0 10px 18px', padding: 0,
                       fontSize: '13px', color: '#444' }}>
            {data.blocked.map(b => (
              <li key={b.symbol}>
                <strong>{b.symbol}</strong> — {b.reason}
              </li>
            ))}
          </ul>
        </>
      )}

      <div style={{ display: 'flex', gap: '8px' }}>
        {authFails.length === 0 && (
          <button
            style={{ ...styles.btnPrimary, opacity: busy ? 0.5 : 1 }}
            onClick={onConfirm}
            disabled={busy}
          >
            Run anyway ({data.ready} symbol{data.ready === 1 ? '' : 's'})
          </button>
        )}
        <button style={styles.btnSecondary} onClick={onCancel} disabled={busy}>
          {authFails.length ? 'Close' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}

function Settings() {
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [success, setSuccess]   = useState(null);

  // Breeze credentials
  const [apiKey, setApiKey]         = useState('');
  const [secretKey, setSecretKey]   = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [hasApiKey, setHasApiKey]       = useState(false);
  const [hasSecretKey, setHasSecretKey] = useState(false);
  const [hasSessionToken, setHasSessionToken] = useState(false);

  //upstox credentials
  const [upstoxApiKey,      setUpstoxApiKey]      = useState('');
  const [upstoxApiSecret,   setUpstoxApiSecret]   = useState('');
  const [upstoxAccessToken, setUpstoxAccessToken] = useState('');
  const [upstoxRedirectUri, setUpstoxRedirectUri] = useState('');
  const [hasUpstoxKey,      setHasUpstoxKey]      = useState(false);
  const [hasUpstoxSecret,   setHasUpstoxSecret]   = useState(false);
  const [hasUpstoxToken,    setHasUpstoxToken]    = useState(false);

  // Market settings
  
  const [scheduleTime, setScheduleTime] = useState('');
  const [callsPerMinute, setCallsPerMinute] = useState('80');
  const [callsPerDay, setCallsPerDay]       = useState('5000');
  const [maxCandles, setMaxCandles]         = useState('1000');

  // Download
  const [downloadSymbols, setDownloadSymbols]     = useState('');
  const [downloadWatchlist, setDownloadWatchlist] = useState('');
  const [downloadTimeframes, setDownloadTimeframes] = useState('5min,1D');
  const [downloadRunning, setDownloadRunning]     = useState(false);
  const [watchlists, setWatchlists]               = useState([]);
  const [downloadSource, setDownloadSource] = useState('all');
  const [showUsageHistory, setShowUsageHistory] = useState(false);
  // API usage panel
  const [apiUsage, setApiUsage] = useState(null);

  // Aggregation
  const [aggSymbols, setAggSymbols]         = useState('');
  const [aggWatchlist, setAggWatchlist]     = useState('');
  const [aggRunning, setAggRunning]         = useState(false);

  // Indicators
  const [indSymbols, setIndSymbols]     = useState('');
  const [indWatchlist, setIndWatchlist] = useState('');
  const [indRunning, setIndRunning]     = useState(false);

  // websocket
   const [wsRunning,     setWsRunning]     = useState(false);
   const [wsSubscribed,  setWsSubscribed]  = useState([]);

   // ── Job polling — shared, with cleanup on unmount ──────────
  const pollTimersRef = useRef({});

  useEffect(() => {
    return () => {
      Object.values(pollTimersRef.current).forEach(clearInterval);
      pollTimersRef.current = {};
    };
  }, []);

  const pollJob = (key, url, label, setRunningFlag) => {
    if (pollTimersRef.current[key]) clearInterval(pollTimersRef.current[key]);
    let errCount = 0;

    const poll = async () => {
      try {
        const res = await api.get(url);
        errCount  = 0;                              // reset on success
        const d   = res.data;

        if (d.running) {
          if (d.last_lines && d.last_lines.length > 0) {
            setSuccess(`${label}: ${d.last_lines[d.last_lines.length - 1]}`);
          }
          return;
        }
        clearInterval(pollTimersRef.current[key]);
        delete pollTimersRef.current[key];
        setRunningFlag(false);
        if (d.ok === false) {
          const why = d.note || `exit code ${d.exit_code}`;
          setError(`${label} failed — ${why} (check log)`);
        } else {
          setSuccess(`${label} completed successfully`);
        }
      } catch (e) {
        errCount += 1;                              // transient — keep polling
        if (errCount >= 5) {
          clearInterval(pollTimersRef.current[key]);
          delete pollTimersRef.current[key];
          setRunningFlag(false);
          setError(`${label}: lost contact with backend — check status manually`);
        }
      }
    };

    poll();
    pollTimersRef.current[key] = setInterval(poll, 5000);
  }; 
  
  
  
  // ============================================================
  // LOAD DATA
  // ============================================================
  useEffect(() => {
    loadSettings();
    loadApiUsage();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Load Breeze credentials status
      const credsRes = await api.get(
        `/settings/breeze/credentials`
      );
      setHasApiKey(credsRes.data.has_api_key);
      setHasSecretKey(credsRes.data.has_secret_key);
      setHasSessionToken(credsRes.data.has_session_token);
      await loadWsStatus()

      // Load Upstox credentials status
      const upRes = await api.get(
        `/settings/upstox/credentials`
      );
      setHasUpstoxKey(upRes.data.has_api_key);
      setHasUpstoxSecret(upRes.data.has_api_secret);
      setHasUpstoxToken(upRes.data.has_access_token);
      setUpstoxRedirectUri(upRes.data.UPSTOX_REDIRECT_URI || '');

      // Load market settings
      const mktRes = await api.get(
        `/settings/market/config`
      );
      const s = mktRes.data;
      
      setScheduleTime(s.DOWNLOAD_SCHEDULE_TIME || '');
      setCallsPerMinute(s.API_CALLS_PER_MINUTE || '80');
      setCallsPerDay(s.API_CALLS_PER_DAY       || '5000');
      setMaxCandles(s.MAX_CANDLES_PER_CALL     || '1000');

      // Load watchlists for download dropdown
      const wlRes = await api.get(`/watchlists`);
      setWatchlists(wlRes.data);

    } catch (err) {
      setError('Failed to load settings');
    }
    setLoading(false);
  };

  const loadApiUsage = async () => {
    try {
      const res = await api.get('/api-usage/summary', { params: { days: 30 } });
      setApiUsage(res.data);
    } catch (err) {
      console.error('API usage fetch failed:', err);
    }
  };

  // ============================================================
  // SAVE FUNCTIONS
  // ============================================================
  const saveSetting = async (key, value) => {
    await api.put(`/settings/${key}`, null, {
      params: { value }
    });
  };

  const saveCredentials = async () => {
    setLoading(true);
    try {
      if (apiKey)       await saveSetting('BREEZE_API_KEY', apiKey);
      if (secretKey)    await saveSetting('BREEZE_SECRET_KEY', secretKey);
      if (sessionToken) await saveSetting('BREEZE_SESSION_TOKEN', sessionToken);

      setApiKey('');
      setSecretKey('');
      setSessionToken('');
      setSuccess('Credentials saved successfully');
      await loadSettings();
    } catch (err) {
      setError('Failed to save credentials');
    }
    setLoading(false);
  };

  const saveUpstoxCredentials = async () => {
    setLoading(true);
    try {
      if (upstoxApiKey)      await saveSetting('UPSTOX_API_KEY', upstoxApiKey);
      if (upstoxApiSecret)   await saveSetting('UPSTOX_API_SECRET', upstoxApiSecret);
      if (upstoxAccessToken) await saveSetting('UPSTOX_ACCESS_TOKEN', upstoxAccessToken);
      if (upstoxRedirectUri) await saveSetting('UPSTOX_REDIRECT_URI', upstoxRedirectUri);

      setUpstoxApiKey('');
      setUpstoxApiSecret('');
      setUpstoxAccessToken('');
      setUpstoxRedirectUri('');
      setSuccess('Upstox credentials saved successfully');
      await loadSettings();
    } catch (err) {
      setError('Failed to save Upstox credentials');
    }
    setLoading(false);
  };
  
  const saveMarketSettings = async () => {
    setLoading(true);
    try {
      
      await saveSetting('DOWNLOAD_SCHEDULE_TIME', scheduleTime);
      await saveSetting('API_CALLS_PER_MINUTE',   callsPerMinute);
      await saveSetting('API_CALLS_PER_DAY',      callsPerDay);
      await saveSetting('MAX_CANDLES_PER_CALL',   maxCandles);
      setSuccess('Market settings saved');
    } catch (err) {
      setError('Failed to save market settings');
    }
    setLoading(false);
  };

  const checkDownloadStatus = async () => {
    try {
      const res = await api.get(`/download/status`);
      setDownloadRunning(res.data.running);
    } catch (err) {
      console.error('Status check failed:', err);
    }
  };

  const [dlPreflight, setDlPreflight] = useState(null);

  const startDownload = async (skipPreflight = false) => {
    if (!skipPreflight) {
      setLoading(true);
      try {
        const pf = await runPreflight(
          downloadSource, downloadSymbols, downloadWatchlist);
        if (!pf.ok) {
          setDlPreflight(pf);
          setLoading(false);
          return;
        }
      } catch (e) {
        setError('Preflight check failed — check backend');
        setLoading(false);
        return;
      }
    }
    setDlPreflight(null);
    setLoading(true);
    try {
      const params = { timeframes: downloadTimeframes, source: downloadSource };
      if (downloadSymbols.trim()) {
        params.symbols = downloadSymbols.trim().toUpperCase();
      } else if (downloadWatchlist) {
        params.watchlist_name = downloadWatchlist;
      }

      const res = await api.post(`/download/start`, null, { params });

      if (res.data.status === 'started') {
        setSuccess(`Download started (PID: ${res.data.pid})`);
        setDownloadRunning(true);
        pollJob('download', '/download/status', 'Download', setDownloadRunning);
      } else if (res.data.status === 'already_running') {
        setError(`Download already running (PID: ${res.data.pid})`);
        setDownloadRunning(true);
        pollJob('download', '/download/status', 'Download', setDownloadRunning);
      } else {
        setError(res.data.message || 'Failed to start download');
      }
    } catch (err) {
      setError('Failed to start download');
    }
    setLoading(false);
  };

  const checkAggStatus = async () => {
    try {
      const res = await api.get(`/aggregate/status`);
      setAggRunning(res.data.running);
    } catch (err) {
      console.error('Agg status check failed:', err);
    }
  };

  const startAggregation = async () => {
    setLoading(true);
    try {
      const params = {};
      if (aggSymbols.trim()) {
        params.symbols = aggSymbols.trim().toUpperCase();
      } else if (aggWatchlist) {
        params.watchlist_name = aggWatchlist;
      }

      const res = await api.post(`/aggregate/start`, null, { params });

      if (res.data.status === 'started') {
        setSuccess(`Aggregation started (PID: ${res.data.pid})`);
        setAggRunning(true);
        pollJob('aggregate', '/aggregate/status', 'Aggregation', setAggRunning);
      } else if (res.data.status === 'already_running') {
        setError(`Aggregation already running (PID: ${res.data.pid})`);
        setAggRunning(true);
        pollJob('aggregate', '/aggregate/status', 'Aggregation', setAggRunning);
      } else {
        setError(res.data.message || 'Failed to start aggregation');
      }
    } catch (err) {
      setError('Failed to start aggregation');
    }
    setLoading(false);
  };
  
  const checkIndStatus = async () => {
    try {
      const res = await api.get(
        `/indicators/calculate/status`
      );
      setIndRunning(res.data.running);
    } catch (err) {
      console.error('Indicator status check failed:', err);
    }
  };

  const startIndicators = async () => {
    setLoading(true);
    try {
      const params = {};
      if (indSymbols.trim()) {
        params.symbols = indSymbols.trim().toUpperCase();
      } else if (indWatchlist) {
        params.watchlist_name = indWatchlist;
      }

      const res = await api.post(`/indicators/calculate`, null, { params });

      if (res.data.status === 'started') {
        setSuccess(`Indicator calculation started (PID: ${res.data.pid})`);
        setIndRunning(true);
        pollJob('indicators', '/indicators/calculate/status',
                'Indicator calculation', setIndRunning);
      } else if (res.data.status === 'already_running') {
        setError(`Indicator calculation already running (PID: ${res.data.pid})`);
        setIndRunning(true);
        pollJob('indicators', '/indicators/calculate/status',
                'Indicator calculation', setIndRunning);
      } else {
        setError(res.data.message || 'Failed to start indicator calculation');
      }
    } catch (err) {
      setError('Failed to start indicator calculation');
    }
    setLoading(false);
  };
  
  //==========================================================
  // websocket section
  //============================================================
  const loadWsStatus = async () => {
    try {
      const res = await api.get(`/websocket/status`);
      setWsRunning(res.data.running);
      setWsSubscribed(res.data.subscribed || []);
    } catch (err) {
      // WebSocket not started yet — normal
    }
  };
 
  const startWebSocket = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/websocket/start`);
      setWsRunning(res.data.running);
      setWsSubscribed(res.data.subscribed || []);
      setSuccess(`WebSocket started — subscribed to: ${(res.data.subscribed || []).join(', ')}`);
    } catch (err) {
      setError('Failed to start WebSocket — check credentials and watchlist');
    }
    setLoading(false);
  };
 
  const stopWebSocket = async () => {
    setLoading(true);
    try {
      await api.post(`/websocket/stop`);
      setWsRunning(false);
      setWsSubscribed([]);
      setSuccess('WebSocket stopped');
    } catch (err) {
      setError('Failed to stop WebSocket');
    }
    setLoading(false);
  };
 
  
  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Settings</h3>

      {/* Messages */}
      {error && (
        <div style={styles.error}>
          {error}
          <button onClick={() => setError(null)} style={styles.closeBtn}>×</button>
        </div>
      )}
      {success && (
        <div style={styles.success}>
          {success}
          <button onClick={() => setSuccess(null)} style={styles.closeBtn}>×</button>
        </div>
      )}

      {/* Breeze API Credentials */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Breeze API Credentials</div>

        {/* Status indicators */}
        <div style={styles.statusRow}>
          <StatusBadge label="API Key"       active={hasApiKey} />
          <StatusBadge label="Secret Key"    active={hasSecretKey} />
          <StatusBadge label="Session Token" active={hasSessionToken} />
        </div>

        <div style={styles.note}>
          Leave field empty to keep existing value.
          Session token must be refreshed daily from Breeze website.
        </div>

        <div style={styles.formGrid}>
          <div style={styles.formField}>
            <label style={styles.label}>API Key</label>
            <input
              style={styles.input}
              type="password"
              placeholder={hasApiKey ? '••••••••' : 'Enter API key'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>Secret Key</label>
            <input
              style={styles.input}
              type="password"
              placeholder={hasSecretKey ? '••••••••' : 'Enter secret key'}
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
            />
          </div>
          <div style={{ ...styles.formField, gridColumn: '1 / -1' }}>
            <label style={styles.label}>
              Session Token
              <span style={styles.dailyBadge}>Refresh Daily</span>
            </label>
            <input
              style={styles.input}
              type="password"
              placeholder={hasSessionToken ? '••••••••' : 'Paste session token'}
              value={sessionToken}
              onChange={(e) => setSessionToken(e.target.value)}
            />
          </div>
        </div>

        <button
          style={styles.btnPrimary}
          onClick={saveCredentials}
          disabled={loading}
        >
          Save Credentials
        </button>
      </div>

      {/* Upstox API Credentials */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Upstox API Credentials</div>

        <div style={styles.statusRow}>
          <StatusBadge label="API Key"      active={hasUpstoxKey} />
          <StatusBadge label="API Secret"   active={hasUpstoxSecret} />
          <StatusBadge label="Access Token" active={hasUpstoxToken} />
        </div>

        <div style={styles.note}>
          Leave field empty to keep existing value.
          Only the access token is used for downloads — it expires daily
          and must be pasted fresh. Key, secret and redirect URI are for
          the future OAuth flow.
        </div>

        <div style={styles.formGrid}>
          <div style={styles.formField}>
            <label style={styles.label}>API Key</label>
            <input
              style={styles.input}
              type="password"
              placeholder={hasUpstoxKey ? '••••••••' : 'Enter API key'}
              value={upstoxApiKey}
              onChange={(e) => setUpstoxApiKey(e.target.value)}
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>API Secret</label>
            <input
              style={styles.input}
              type="password"
              placeholder={hasUpstoxSecret ? '••••••••' : 'Enter API secret'}
              value={upstoxApiSecret}
              onChange={(e) => setUpstoxApiSecret(e.target.value)}
            />
          </div>
          <div style={{ ...styles.formField, gridColumn: '1 / -1' }}>
            <label style={styles.label}>
              Access Token
              <span style={styles.dailyBadge}>Refresh Daily</span>
            </label>
            <input
              style={styles.input}
              type="password"
              placeholder={hasUpstoxToken ? '••••••••' : 'Paste access token'}
              value={upstoxAccessToken}
              onChange={(e) => setUpstoxAccessToken(e.target.value)}
            />
          </div>
          <div style={{ ...styles.formField, gridColumn: '1 / -1' }}>
            <label style={styles.label}>
              Redirect URI
              <span style={styles.optionalBadge}>Optional</span>
            </label>
            <input
              style={styles.input}
              placeholder="e.g. https://your-host/upstox/callback"
              value={upstoxRedirectUri}
              onChange={(e) => setUpstoxRedirectUri(e.target.value)}
            />
          </div>
        </div>

        <button
          style={styles.btnPrimary}
          onClick={saveUpstoxCredentials}
          disabled={loading}
        >
          Save Upstox Credentials
        </button>
      </div>

      {/* 4. Add this JSX card AFTER the Breeze API Credentials card
      //    and BEFORE the Market Settings card:*/}
 
      {/* Live WebSocket Feed */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Live WebSocket Feed</div>
 
        <div style={styles.statusRow}>
          <div style={{
            ...styles.badge,
            backgroundColor: wsRunning ? '#e8f5e9' : '#fff8e1',
            color:           wsRunning ? '#2e7d32' : '#f59e0b',
            padding: '4px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
          }}>
            {wsRunning ? '● Live' : '○ Stopped'}
          </div>
          {wsRunning && wsSubscribed.length > 0 && (
            <span style={{ fontSize: '12px', color: '#666' }}>
              Subscribed: {wsSubscribed.join(', ')}
            </span>
          )}
        </div>
 
        <div style={styles.note}>
          WebSocket streams live tick data during market hours.
          Symbols are sourced from watchlists with WebSocket enabled.
          Make sure Breeze credentials are saved before starting.
        </div>
 
        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          <button
            style={{
              ...styles.btnPrimary,
              backgroundColor: wsRunning ? '#ccc' : '#2e7d32',
              cursor: wsRunning ? 'not-allowed' : 'pointer',
            }}
            onClick={startWebSocket}
            disabled={wsRunning || loading}
          >
            ▶ Start WebSocket
          </button>
          <button
            style={{
              ...styles.btnPrimary,
              backgroundColor: !wsRunning ? '#ccc' : '#d32f2f',
              cursor: !wsRunning ? 'not-allowed' : 'pointer',
            }}
            onClick={stopWebSocket}
            disabled={!wsRunning || loading}
          >
            ■ Stop WebSocket
          </button>
          <button
            style={styles.btnSecondary}
            onClick={loadWsStatus}
          >
            ↻ Refresh Status
          </button>
        </div>
      </div>
      
      
      
      {/* Market Settings */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Market Settings</div>

        <div style={styles.formGrid}>
          
        </div>

        <div style={styles.cardTitle} >
          Download Schedule
        </div>

        <div style={styles.formGrid}>
          <div style={styles.formField}>
            <label style={styles.label}>
              Auto Download Time (HH:MM)
              <span style={styles.optionalBadge}>Optional</span>
            </label>
            <input
              style={styles.input}
              placeholder="e.g. 16:30 — leave empty for manual only"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
            />
          </div>
        </div>

        <div style={styles.cardTitle}>
          API Limits
        </div>

        <div style={styles.formGrid}>
          <div style={styles.formField}>
            <label style={styles.label}>Max Calls Per Minute</label>
            <input
              style={styles.input}
              type="number"
              value={callsPerMinute}
              onChange={(e) => setCallsPerMinute(e.target.value)}
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>Max Calls Per Day</label>
            <input
              style={styles.input}
              type="number"
              value={callsPerDay}
              onChange={(e) => setCallsPerDay(e.target.value)}
            />
          </div>
          
          <div style={styles.formField}>
            <label style={styles.label}>Max Candles Per Call</label>
            <input
              style={styles.input}
              type="number"
              value={maxCandles}
              onChange={(e) => setMaxCandles(e.target.value)}
            />
          </div>
        </div>

        <button
          style={styles.btnPrimary}
          onClick={saveMarketSettings}
          disabled={loading}
        >
          Save Settings
        </button>
      </div>

      {/* API Usage — from api_call_log */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>API Usage</div>

        <div style={styles.statusRow}>
          <button style={styles.btnSecondary} onClick={loadApiUsage}>
            Refresh Usage
          </button>
        </div>

        {!apiUsage ? (
          <div style={styles.note}>Loading usage…</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '24px', margin: '8px 0 14px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Breeze today
                </div>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                  {apiUsage.today.breeze}
                  <span style={{ fontSize: '13px', color: '#888',
                                 fontWeight: 'normal' }}>
                    {' '}/ {callsPerDay || 5000}
                  </span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Upstox today
                </div>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                  {apiUsage.today.upstox}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Upstox last 30 min
                </div>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                  {apiUsage.upstox_30min}
                  <span style={{ fontSize: '13px', color: '#888',
                                 fontWeight: 'normal' }}>
                    {' '}/ 1000
                  </span>
                </div>
              </div>
            </div>

            <button
              style={{ ...styles.btnSecondary, marginTop: '4px' }}
              onClick={() => setShowUsageHistory(v => !v)}
            >
              {showUsageHistory ? 'Hide 30-day history ▲' : 'Show 30-day history ▼'}
            </button>

            {showUsageHistory && (
              <div style={{ maxHeight: '220px', overflowY: 'auto',
                            marginTop: '10px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse',
                                fontSize: '13px' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#666' }}>
                      <th style={{ padding: '4px 8px' }}>Date</th>
                      <th style={{ padding: '4px 8px' }}>Provider</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right' }}>Total</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right' }}>OK</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right' }}>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiUsage.report.length === 0 ? (
                      <tr><td colSpan={5} style={{ padding: '8px',
                            color: '#888' }}>No calls logged yet.</td></tr>
                    ) : (
                      apiUsage.report.map((r, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                          <td style={{ padding: '4px 8px' }}>{r.call_date}</td>
                          <td style={{ padding: '4px 8px' }}>{r.provider}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                            {r.total_calls}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                            {r.ok_calls}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right',
                                       color: r.error_calls > 0 ? '#c0392b' : '#444' }}>
                            {r.error_calls}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <PipelineCard watchlists={watchlists} />
      
      {/* Download */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Historical Data Download</div>

        <div style={styles.statusRow}>
          <StatusBadge 
            label="Download" 
            active={downloadRunning} 
          />
          <button
            style={styles.btnSecondary}
            onClick={checkDownloadStatus}
          >
            Refresh Status
          </button>
        </div>

        <div style={styles.formGrid}>
          <div style={styles.formField}>
            <label style={styles.label}>Data Source</label>
            <select
              style={styles.input}
              value={downloadSource}
              onChange={(e) => setDownloadSource(e.target.value)}
            >
              <option value="breeze">Breeze</option>
              <option value="upstox">Upstox</option>
              <option value="all">All (per stock default)</option>
            </select>
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>
              Symbols (comma separated)
            </label>
            <input
              style={styles.input}
              placeholder="e.g. RELIND,INFY,TCS"
              value={downloadSymbols}
              onChange={(e) => setDownloadSymbols(e.target.value)}
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>
              OR Select Watchlist
            </label>
            <select
              style={styles.input}
              value={downloadWatchlist}
              onChange={(e) => setDownloadWatchlist(e.target.value)}
            >
              <option value="">-- Select Watchlist --</option>
              {watchlists.map((wl) => (
                <option key={wl.id} value={wl.name}>
                  {wl.name}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>Timeframes</label>
            <input
              style={styles.input}
              placeholder="e.g. 5min,1D"
              value={downloadTimeframes}
              onChange={(e) => setDownloadTimeframes(e.target.value)}
            />
          </div>
        </div>

        <div style={styles.note}>
          Symbols field takes priority over watchlist selection.
          Leave both empty to download all watchlists with download enabled.
        </div>

        <button
          style={{
            ...styles.btnPrimary,
            opacity: downloadRunning ? 0.6 : 1,
            cursor:  downloadRunning ? 'not-allowed' : 'pointer',
          }}
          onClick={startDownload}
          disabled={downloadRunning || loading}
        >
          {downloadRunning ? 'Download Running...' : 'Start Download'}
        </button>

        <PreflightPanel
          data={dlPreflight}
          busy={loading}
          onConfirm={() => startDownload(true)}
          onCancel={() => setDlPreflight(null)}
        />
      </div>

      {/* Aggregation */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Candle Aggregation</div>
        <div style={styles.note}>
          Builds 15min, 1H from 5min data and 1W from 1D data.
          Run after historical data download completes.
        </div>

        <div style={styles.statusRow}>
          <StatusBadge
            label="Aggregation"
            active={aggRunning}
          />
          <button
            style={styles.btnSecondary}
            onClick={checkAggStatus}
          >
            Refresh Status
          </button>
        </div>

        <div style={styles.formGrid}>
          <div style={styles.formField}>
            <label style={styles.label}>
              Symbols (comma separated)
            </label>
            <input
              style={styles.input}
              placeholder="e.g. RELIND,TCS"
              value={aggSymbols}
              onChange={(e) => setAggSymbols(e.target.value)}
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>
              OR Select Watchlist
            </label>
            <select
              style={styles.input}
              value={aggWatchlist}
              onChange={(e) => setAggWatchlist(e.target.value)}
            >
              <option value="">-- Select Watchlist --</option>
              {watchlists.map((wl) => (
                <option key={wl.id} value={wl.name}>
                  {wl.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          style={{
            ...styles.btnPrimary,
            opacity: aggRunning ? 0.6 : 1,
            cursor:  aggRunning ? 'not-allowed' : 'pointer',
          }}
          onClick={startAggregation}
          disabled={aggRunning || loading}
        >
          {aggRunning ? 'Aggregation Running...' : 'Start Aggregation'}
        </button>
      </div>

      {/* Indicator Calculation */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>
          Indicator Calculation
        </div>
        <div style={styles.note}>
          Calculates EMA 5,9,20,50,200 for close, high and low.
          Run after aggregation completes.
        </div>

        <div style={styles.statusRow}>
          <StatusBadge
            label="Indicators"
            active={indRunning}
          />
          <button
            style={styles.btnSecondary}
            onClick={checkIndStatus}
          >
            Refresh Status
          </button>
        </div>

        <div style={styles.formGrid}>
          <div style={styles.formField}>
            <label style={styles.label}>
              Symbols (comma separated)
            </label>
            <input
              style={styles.input}
              placeholder="e.g. RELIND,TCS"
              value={indSymbols}
              onChange={(e) => setIndSymbols(e.target.value)}
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>
              OR Select Watchlist
            </label>
            <select
              style={styles.input}
              value={indWatchlist}
              onChange={(e) => setIndWatchlist(e.target.value)}
            >
              <option value="">-- Select Watchlist --</option>
              {watchlists.map((wl) => (
                <option key={wl.id} value={wl.name}>
                  {wl.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          style={{
            ...styles.btnPrimary,
            opacity: indRunning ? 0.6 : 1,
            cursor:  indRunning ? 'not-allowed' : 'pointer',
          }}
          onClick={startIndicators}
          disabled={indRunning || loading}
        >
          {indRunning ? 'Calculating...' : 'Calculate Indicators'}
        </button>
      </div>

    </div>
  );
}

// ============================================================
// PIPELINE CARD — chained run of selected stages
// ============================================================
function PipelineCard({ watchlists }) {
  const ALL_STAGES = [
    { key: 'download',   label: 'Download' },
    { key: 'aggregate',  label: 'Aggregate' },
    { key: 'indicators', label: 'Indicators' },
    { key: 'crossovers', label: 'Crossovers' },
    { key: 'vpatterns',  label: 'V Patterns' },
  ];

  const [stages, setStages] = useState({
    download: true, aggregate: true, indicators: true,
    crossovers: true, vpatterns: true,
  });
  const [symbols,   setSymbols]   = useState('');
  const [watchlist, setWatchlist] = useState('');
  const [dlTf,      setDlTf]      = useState('5min,1D');
  const [calcTf,    setCalcTf]    = useState('5min,15min,1H,1D,1W');
  const [running,   setRunning]   = useState(false);
  const [msg,       setMsg]       = useState('');
  const timerRef                  = useRef(null);
  const [source, setSource] = useState('all');

  // ── Wedged-detection + kill-switch state ──
  const [startedAt,   setStartedAt]   = useState(null);   // ISO from status
  const [wedged,      setWedged]      = useState(false);  // stall/ceiling tripped
  const [killStep,    setKillStep]    = useState(0);      // 0=hidden 1=stop 2=cancel 3=clear 4=done
  const killStepRef   = useRef(0);
  const [killMsg,     setKillMsg]     = useState('');
  const [killBusy,    setKillBusy]    = useState(false);
  const lastLineRef   = useRef({ text: '', at: 0 });      // tracks log-tail change time

  const STALL_MS   = 10 * 60 * 1000;   // 10 min with no new log line
  const CEILING_MS = 25 * 60 * 1000;   // 25 min hard ceiling

  useEffect(() => {
    // Detect a pipeline that is already running (e.g. after page reload)
    api.get('/pipeline/status')
      .then(res => {
        if (res.data.running) { setRunning(true); startPolling(); }
      })
      .catch(() => {});
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    let errCount = 0;

    const poll = async () => {
      try {
        const res = await api.get('/pipeline/status');
        errCount  = 0;
        const d   = res.data;

        if (d.started_at) setStartedAt(d.started_at);

        const line = (d.last_lines && d.last_lines.length > 0)
          ? d.last_lines[d.last_lines.length - 1] : '';
        if (line) setMsg(line);

        // ── wedged detection (only while genuinely running) ──
        if (d.running) {
          const now = Date.now();
          // track when the log tail last changed
          if (line !== lastLineRef.current.text) {
            lastLineRef.current = { text: line, at: now };
          }
          const stalled = lastLineRef.current.at > 0 &&
                          (now - lastLineRef.current.at) > STALL_MS;
          // Use server-computed elapsed time (seconds) — no tz parsing.
          const overCeiling = (d.elapsed_seconds != null) &&
                              (d.elapsed_seconds * 1000 > CEILING_MS);
          
          setWedged(stalled || overCeiling);
        } else {
          setWedged(false);
        }

        if (!d.running) {
          clearInterval(timerRef.current);
          // If the user is walking the kill steps, don't tear down the
          // panel or overwrite its messaging — let them finish steps 2-3.
          if (killStepRef.current === 0) {
            setRunning(false);
            setMsg(d.ok === false
              ? `❌ Pipeline failed — ${d.note || `exit code ${d.exit_code}`} (check pipeline.log)`
              : '✅ Pipeline completed successfully');
          }
        }
      } catch (e) {
        errCount += 1;
        if (errCount >= 5) {
          clearInterval(timerRef.current);
          setRunning(false);
          setMsg('⚠ Lost contact with backend — check status manually');
        }
      }
    };

    poll();
    timerRef.current = setInterval(poll, 5000);
  };

  const toggleStage = (key) =>
    setStages(s => ({ ...s, [key]: !s[key] }));

  const [pfData, setPfData] = useState(null);
  const [pfBusy, setPfBusy] = useState(false);

  const run = async (skipPreflight = false) => {
    const selected = ALL_STAGES.map(s => s.key).filter(k => stages[k]);
    if (selected.length === 0) {
      setMsg('Select at least one stage');
      return;
    }

    // Only meaningful if the download stage is actually selected
    if (!skipPreflight && selected.includes('download')) {
      setPfBusy(true);
      try {
        const pf = await runPreflight(source, symbols, watchlist);
        setPfBusy(false);
        if (!pf.ok) {
          setPfData(pf);
          setMsg('');
          return;
        }
      } catch (e) {
        setPfBusy(false);
        setMsg('Preflight check failed — check backend');
        return;
      }
    }

    setPfData(null);
    setMsg('Starting pipeline...');
    resetKill();
    try {
      const params = {
        stages:          selected.join(','),
        dl_timeframes:   dlTf,
        calc_timeframes: calcTf,
        source:          source,
      };
      if (symbols.trim())  params.symbols        = symbols.trim().toUpperCase();
      else if (watchlist)  params.watchlist_name = watchlist;

      const res = await api.post('/pipeline/start', null, { params });

      if (res.data.status === 'started') {
        setRunning(true);
        setMsg(`Pipeline running (PID: ${res.data.pid})...`);
        startPolling();
      } else if (res.data.status === 'already_running') {
        setRunning(true);
        setMsg(`Pipeline already running (PID: ${res.data.pid})`);
        startPolling();
      } else {
        setMsg(`Error: ${res.data.message || 'failed to start'}`);
      }
    } catch (e) {
      setMsg('Failed to start — check backend');
    }
  };

  const bumpKillStep = (n) => { killStepRef.current = n; setKillStep(n); };

  // ── Kill-switch: reset log-tail tracker whenever a run starts ──
  const resetKill = () => {
    bumpKillStep(0); setKillMsg(''); setKillBusy(false);
    setWedged(false);
    lastLineRef.current = { text: '', at: 0 };
  };

  

  // Step 1 — stop the process tree
  const killStop = async () => {
    setKillBusy(true);
    setKillMsg('Stopping the pipeline process...');
    try {
      const res = await api.post('/jobs/pipeline/stop', null, { timeout: 30000 });
      const d = res.data;
      setKillMsg(`✔ ${d.message || 'Stopped.'} Now check for a stuck database query.`);
      bumpKillStep(2);
    } catch (e) {
      setKillMsg('❌ Stop failed — the backend may be unreachable. You can retry.');
    }
    setKillBusy(false);
  };

  // Step 2 — cancel any stuck DB query
  const killCancelDb = async () => {
    setKillBusy(true);
    setKillMsg('Checking the database for a stuck query...');
    try {
      const res = await api.post('/jobs/pipeline/cancel-db', null, { timeout: 30000 });
      const d = res.data;
      setKillMsg(d.count > 0
        ? `✔ Cancelled ${d.count} database query(s). Now clear the status.`
        : `✔ ${d.message || 'No stuck query found.'} Now clear the status.`);
      bumpKillStep(3);
    } catch (e) {
      setKillMsg('❌ Could not check the database — you can retry, or skip to Clear.');
      bumpKillStep(3);   // allow proceeding to clear even if this errored
    }
    setKillBusy(false);
  };

  // Step 3 — clear the status file so the UI resets
  const killClear = async () => {
    setKillBusy(true);
    setKillMsg('Clearing job status...');
    try {
      // Stop polling FIRST so a stray poll can't overwrite the final
      // message or re-flip running state after we clear.
      if (timerRef.current) clearInterval(timerRef.current);
      await api.post('/jobs/pipeline/clear', null, { timeout: 30000 });
      setRunning(false);
      setMsg('');                 // clear the poll-driven message
      setKillMsg('✔ Cleared. Pipeline is safe to run again.');
      bumpKillStep(4);
    } catch (e) {
      setKillMsg('❌ Clear failed — you may need to clear the status file manually.');
    }
    setKillBusy(false);
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>Run Pipeline (chained)</div>
      <div style={styles.note}>
        Runs the selected stages sequentially in the correct order:
        Download → Aggregate → Indicators → Crossovers → V Patterns.
        Stops at the first failure. Individual cards below still work
        for step-by-step runs. Log: pipeline.log
      </div>

      <div style={{ display: 'flex', gap: '14px',
                    flexWrap: 'wrap', margin: '10px 0' }}>
        {ALL_STAGES.map(s => (
          <label key={s.key}
            style={{ fontSize: '13px', display: 'flex',
                     alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
            <input type="checkbox"
              checked={stages[s.key]}
              onChange={() => toggleStage(s.key)}
              disabled={running} />
            {s.label}
          </label>
        ))}
      </div>

      <div style={styles.formGrid}>
        <div style={styles.formField}>
          <label style={styles.label}>Symbols (comma separated)</label>
          <input style={styles.input} placeholder="e.g. RELIND,TCS"
            value={symbols}
            onChange={e => { setSymbols(e.target.value); }}
            disabled={running} />
        </div>
        <div style={styles.formField}>
          <label style={styles.label}>OR Select Watchlist</label>
          <select style={styles.input} value={watchlist}
            onChange={e => setWatchlist(e.target.value)}
            disabled={running}>
            <option value="">-- Select Watchlist --</option>
            {watchlists.map(wl => (
              <option key={wl.id} value={wl.name}>{wl.name}</option>
            ))}
          </select>
        </div>
        <div style={styles.formField}>
          <label style={styles.label}>Download Timeframes</label>
          <input style={styles.input} value={dlTf}
            onChange={e => setDlTf(e.target.value)}
            disabled={running} />
        </div>
        <div style={styles.formField}>
          <label style={styles.label}>Calc Timeframes</label>
          <input style={styles.input} value={calcTf}
            onChange={e => setCalcTf(e.target.value)}
            disabled={running} />
        </div>
        <div style={{ ...styles.formField, gridColumn: '1 / -1' }}>
          <label style={styles.label}>Data Source</label>
          <select style={styles.input} value={source}
            onChange={e => setSource(e.target.value)}
            disabled={running}>
            <option value="breeze">Breeze</option>
            <option value="upstox">Upstox</option>
            <option value="all">All (per stock default)</option>
          </select>
        </div>
      </div>

      <button
        style={{ ...styles.btnPrimary,
                 opacity: running ? 0.6 : 1,
                 cursor:  running ? 'not-allowed' : 'pointer' }}
        onClick={() => run()}
        disabled={running}>
        {running ? 'Pipeline Running...' : 'Run Pipeline'}
      </button>

      <PreflightPanel
        data={pfData}
        busy={pfBusy}
        onConfirm={() => run(true)}
        onCancel={() => setPfData(null)}
      />

      {msg && (
        <div style={{
          marginTop: '8px', fontSize: '13px',
          color: msg.startsWith('✅') ? '#2e7d32'
               : msg.startsWith('❌') ? '#d32f2f' : '#444',
        }}>
          {msg}
        </div>
      )}

      {/* ── Wedged warning + guided kill panel ── */}
      {running && (wedged || killStep > 0) && (
        <div style={{
          marginTop: '12px', padding: '12px 14px',
          border: '1px solid #f0c36d', borderRadius: '6px',
          background: '#fff8e6',
        }}>
          {killStep === 0 && (
            <div style={{ fontSize: '13px', color: '#8a6d1a', marginBottom: '8px' }}>
              ⚠ This pipeline hasn't shown progress for a while — it may be stuck.
              You can stop it safely below. Each step tells you what to do next.
            </div>
          )}

          {killMsg && (
            <div style={{ fontSize: '13px', color: '#444', marginBottom: '8px' }}>
              {killMsg}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {/* Step 1 */}
            <button
              onClick={killStop}
              disabled={killBusy || killStep > 1}
              style={{
                ...styles.btnPrimary,
                background: '#c0392b',
                opacity: (killBusy || killStep > 1) ? 0.5 : 1,
                cursor:  (killBusy || killStep > 1) ? 'not-allowed' : 'pointer',
              }}>
              1. Stop job
            </button>

            {/* Step 2 — appears after stop */}
            {killStep >= 2 && (
              <button
                onClick={killCancelDb}
                disabled={killBusy || killStep > 2}
                style={{
                  ...styles.btnPrimary,
                  opacity: (killBusy || killStep > 2) ? 0.5 : 1,
                  cursor:  (killBusy || killStep > 2) ? 'not-allowed' : 'pointer',
                }}>
                2. Check / cancel DB query
              </button>
            )}

            {/* Step 3 — appears after cancel-db */}
            {killStep >= 3 && (
              <button
                onClick={killClear}
                disabled={killBusy || killStep > 3}
                style={{
                  ...styles.btnPrimary,
                  opacity: (killBusy || killStep > 3) ? 0.5 : 1,
                  cursor:  (killBusy || killStep > 3) ? 'not-allowed' : 'pointer',
                }}>
                3. Clear status
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}





// ============================================================
// STATUS BADGE COMPONENT
// ============================================================
function StatusBadge({ label, active }) {
  return (
    <div style={styles.badge}>
      <span style={{
        ...styles.dot,
        backgroundColor: active ? '#2e7d32' : '#d32f2f'
      }} />
      <span style={styles.badgeLabel}>{label}</span>
      <span style={{
        ...styles.badgeStatus,
        color: active ? '#2e7d32' : '#d32f2f'
      }}>
        {active ? 'Set' : 'Not Set'}
      </span>
    </div>
  );
}

const styles = {
  container: {
    padding:   '16px',
    overflowY: 'auto',
    height:    '100%',
    background:'#f5f5f5',
    color:     '#131722',
  },
  title: {
    fontSize:     '18px',
    fontWeight:   'bold',
    marginBottom: '16px',
    color:        '#131722',
  },
  card: {
    backgroundColor: '#ffffff',
    border:          '1px solid #e0e0e0',
    borderRadius:    '8px',
    padding:         '16px',
    marginBottom:    '16px',
    boxShadow:       '0 1px 3px rgba(0,0,0,0.08)',
  },
  cardTitle: {
    color:         '#666',
    fontSize:      '11px',
    textTransform: 'uppercase',
    marginBottom:  '12px',
    marginTop:     '12px',
    letterSpacing: '0.5px',
    fontWeight:    'bold',
  },
  statusRow: {
    display:      'flex',
    gap:          '16px',
    marginBottom: '12px',
    flexWrap:     'wrap',
  },
  badge: {
    display:         'flex',
    alignItems:      'center',
    gap:             '6px',
    padding:         '6px 12px',
    backgroundColor: '#f9f9f9',
    border:          '1px solid #e0e0e0',
    borderRadius:    '20px',
  },
  dot: {
    width:        '8px',
    height:       '8px',
    borderRadius: '50%',
  },
  badgeLabel: {
    fontSize: '12px',
    color:    '#333',
  },
  badgeStatus: {
    fontSize:   '12px',
    fontWeight: 'bold',
  },
  note: {
    fontSize:     '12px',
    color:        '#888',
    marginBottom: '12px',
    fontStyle:    'italic',
  },
  dailyBadge: {
    marginLeft:      '8px',
    padding:         '2px 6px',
    backgroundColor: '#fff3e0',
    color:           '#e65100',
    borderRadius:    '3px',
    fontSize:        '10px',
    fontWeight:      'bold',
  },
  optionalBadge: {
    marginLeft:      '8px',
    padding:         '2px 6px',
    backgroundColor: '#f0f0f0',
    color:           '#666',
    borderRadius:    '3px',
    fontSize:        '10px',
  },
  formGrid: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gap:                 '10px',
    marginBottom:        '12px',
  },
  formField: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '4px',
  },
  label: {
    fontSize: '12px',
    color:    '#666',
  },
  input: {
    padding:      '7px 10px',
    border:       '1px solid #ddd',
    borderRadius: '4px',
    fontSize:     '13px',
    color:        '#131722',
    background:   '#fff',
    width:        '100%',
    boxSizing:    'border-box',
  },
  btnPrimary: {
    padding:         '8px 20px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontSize:        '13px',
    fontWeight:      'bold',
  },
  error: {
    backgroundColor: '#fff0f0',
    color:           '#d32f2f',
    padding:         '10px 14px',
    borderRadius:    '4px',
    marginBottom:    '12px',
    fontSize:        '13px',
    display:         'flex',
    justifyContent:  'space-between',
    border:          '1px solid #ffcdd2',
  },
  success: {
    backgroundColor: '#f0fff4',
    color:           '#2e7d32',
    padding:         '10px 14px',
    borderRadius:    '4px',
    marginBottom:    '12px',
    fontSize:        '13px',
    display:         'flex',
    justifyContent:  'space-between',
    border:          '1px solid #c8e6c9',
  },
  closeBtn: {
    background: 'none',
    border:     'none',
    cursor:     'pointer',
    fontSize:   '16px',
    color:      'inherit',
  },

  btnSecondary: {
    padding:         '6px 12px',
    backgroundColor: '#f0f0f0',
    color:           '#333',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontSize:        '12px',
  },
};

export default Settings;